// The staff side of the four visa decisions (API docs/visa-decisions.md,
// migration 023): the decision record, the call after a refusal, the codes
// the customer sends from the official site, the host's attestation link and
// fees the customer paid outside Safrati. Before the migration every new key
// is null (or absent on an older API) and these all answer "nothing to show".

import { dateTime, parseStoredDate } from "./format";
import { wallWords } from "./filingWords";
import { isoFromTyped, todayIso, typedDayError, typedFromIso } from "src/components/shared/days";

// The Schengen refusal form's grounds (Visa Code Annex VI), in the form's
// order, with short titles for staff matching the letter. The API holds the
// customer's wording (plain English and Arabic); these are only the CMS's.
export const REFUSAL_GROUNDS = [
  { key: "false_document", title: "False or forged travel document" },
  { key: "purpose_not_justified", title: "Purpose of the stay not justified" },
  { key: "no_means_stay", title: "Not enough money for the stay and return" },
  { key: "no_means_transit", title: "Not enough money to reach the destination (transit)" },
  { key: "days_used", title: "Already stayed 90 days in the last 180" },
  { key: "sis_alert", title: "Alert in the Schengen Information System" },
  { key: "threat", title: "Seen as a threat (public order, security, health)" },
  { key: "no_insurance", title: "No valid travel medical insurance" },
  { key: "unreliable_info", title: "Information on the stay not reliable" },
  { key: "not_reliable_documents", title: "Documents or their content doubted" },
  { key: "intention_to_leave", title: "Intention to leave in time not shown" },
  { key: "other", title: "Another reason (write it in the note)" },
];

// The grounds a refusal can be recorded with depend on the kind of visa
// (QA nadia-23). Annex VI is the Schengen short-stay refusal form: only a
// Schengen short-stay case (the case's official form) gets all of it.
// Anything else (a UAE e-Visa, a UK visa, a national long-stay visa) gets
// the grounds that are not Schengen rules and whose words for the customer
// (the API's shared/fields/refusalGrounds.js) don't name the Schengen area,
// plus "Another reason", written from the letter in the note. Nothing here
// is a new ground: these are the API's own keys, so the customer's page and
// the stats still explain and count them.
export const GENERIC_GROUND_KEYS = ["false_document", "purpose_not_justified", "no_means_stay", "unreliable_info", "not_reliable_documents", "other"];
export const isSchengenCase = (caseData) => caseData?.official_form?.key === "schengen_short_stay";

// The grounds to offer on this case, in the form's order. A ground already
// ticked on the record stays offered, so it can be unticked.
export function groundsFor(caseData, ticked = []) {
  if (isSchengenCase(caseData)) return REFUSAL_GROUNDS;
  const keys = new Set([...GENERIC_GROUND_KEYS, ...ticked]);
  return REFUSAL_GROUNDS.filter((g) => keys.has(g.key));
}

const GROUND_TITLES = Object.fromEntries(REFUSAL_GROUNDS.map((g) => [g.key, g.title]));
export const groundTitle = (key) => GROUND_TITLES[key] || String(key || "").replace(/_/g, " ");

export const ENTRIES = [
  { value: "single", label: "Single entry" },
  { value: "double", label: "Double entry" },
  { value: "multiple", label: "Multiple entry" },
];
const ENTRY_WORDS = Object.fromEntries(ENTRIES.map((e) => [e.value, e.label]));

// The case's own status words for a decision: the API's outcome is
// approved | refused, the case status approved | rejected.
export const outcomeOfStatus = (status) => (status === "approved" ? "approved" : status === "rejected" ? "refused" : null);

// Whether this API knows about decisions at all: an older API has no
// `decision` key on the case; a newer one sends null until there is a record
// (or until migration 023).
export const decisionsKnown = (caseData) => !!caseData && caseData.decision !== undefined;

const parseJson = (value) => {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

// The ground keys on a record, in the form's order. The API may send keys
// or the customer's shape ([{ key, title, … }]), or the stored JSON text.
export function groundKeys(grounds) {
  const list = parseJson(grounds);
  if (!Array.isArray(list)) return [];
  const keys = new Set(list.map((g) => (g && typeof g === "object" ? g.key : g)).filter(Boolean));
  const known = REFUSAL_GROUNDS.map((g) => g.key).filter((k) => keys.has(k));
  const unknown = [...keys].filter((k) => !GROUND_TITLES[k]);
  return [...known, ...unknown];
}

export function grantedOf(decision) {
  const granted = parseJson(decision?.granted);
  return granted && typeof granted === "object" ? granted : null;
}

// A stored DATE ("2026-11-12", or the driver's "2026-11-11T22:00:00.000Z"
// for local midnight east of UTC) as "2026-11-12", or "".
export function isoOfStored(value) {
  const day = parseStoredDate(value);
  if (day == null) return "";
  const d = new Date(day);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
// "12 Nov 2026" from a stored DATE, or "".
export function dayOf(value) {
  const iso = isoOfStored(value);
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]} ${y}`;
}

// A time from the API, for reading. Filing times are Beirut wall-clock text
// ("2026-10-29 14:02", shown as written); event times are instants.
const WALL = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?$/;
export function timeWords(value) {
  if (!value) return "";
  const text = String(value).trim();
  return WALL.test(text) ? wallWords(text) : dateTime(text);
}

// When an API time happened, in ms: an instant as it is, wall-clock text as
// this computer's local time (the team works in Beirut time).
export function instantOf(value) {
  if (!value) return NaN;
  const text = String(value).trim();
  const wall = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(text);
  if (wall) {
    const [, y, mo, d, h, mi, s] = wall.map(Number);
    return new Date(y, mo - 1, d, h, mi, s || 0).getTime();
  }
  return new Date(text).getTime();
}

// ---- the decision form ------------------------------------------------------

// The form's first values: the record being edited, else the visa's last
// consulate, today as the day the customer was told.
export function decisionForm({ outcome, decision = null, consulate = "", now = new Date(), valid_until = "" } = {}) {
  const granted = grantedOf(decision) || {};
  return {
    outcome,
    grounds: groundKeys(decision?.grounds),
    consulate: decision?.consulate || consulate || "",
    decided_on: typedFromIso(isoOfStored(decision?.decided_on)),
    notified_on: decision ? typedFromIso(isoOfStored(decision.notified_on)) : typedFromIso(todayIso(now)),
    note: decision?.note || "",
    entries: granted.entries || "",
    valid_from: typedFromIso(isoOfStored(granted.valid_from)),
    valid_until: typedFromIso(isoOfStored(granted.valid_until || valid_until)),
    days: granted.days == null ? "" : String(granted.days),
  };
}

const DAYS = /^\d{1,4}$/;

// What is wrong with the form, field by field (empty when it can be saved).
export function decisionErrors(form, now = new Date()) {
  const errors = {};
  const past = (name) => {
    const error = typedDayError(form[name], { now });
    if (error) errors[name] = error;
  };
  past("decided_on");
  past("notified_on");
  const decided = isoFromTyped(form.decided_on);
  const notified = isoFromTyped(form.notified_on);
  if (!errors.notified_on && decided && notified && notified < decided) errors.notified_on = "The customer can’t be told before the decision date.";
  if (form.outcome === "refused") {
    if (!form.grounds.length) errors.grounds = "Tick at least one ground, as on the letter.";
    else if (form.grounds.includes("other") && !form.note.trim()) errors.note = "Write the other reason from the letter.";
  }
  if (form.outcome === "approved") {
    ["valid_from", "valid_until"].forEach((name) => {
      const error = typedDayError(form[name], { future: true, now });
      if (error) errors[name] = error;
    });
    const from = isoFromTyped(form.valid_from);
    const until = isoFromTyped(form.valid_until);
    if (!errors.valid_until && from && until && until < from) errors.valid_until = "This is before the first valid day.";
    const days = String(form.days ?? "").trim();
    if (days && (!DAYS.test(days) || Number(days) < 1)) errors.days = "Whole days, like 90.";
  }
  return errors;
}

// What the API takes as `decision` (POST /cases/status) or as the record
// (POST /cases/decision): only the outcome's own fields, empty as null.
export function decisionPayload(form) {
  const text = (value) => String(value ?? "").trim() || null;
  const day = (value) => isoFromTyped(value) || null;
  const refused = form.outcome === "refused";
  const days = String(form.days ?? "").trim();
  return {
    grounds: refused ? REFUSAL_GROUNDS.map((g) => g.key).filter((k) => form.grounds.includes(k)) : [],
    consulate: text(form.consulate),
    decided_on: day(form.decided_on),
    notified_on: day(form.notified_on),
    granted: refused
      ? null
      : {
          entries: form.entries || null,
          valid_from: day(form.valid_from),
          valid_until: day(form.valid_until),
          days: days ? Number(days) : null,
          limited: false,
        },
    note: text(form.note),
  };
}

// "Multiple entry · 1 Dec 2026 to 30 Nov 2027 · 90 days", or "".
export function grantedWords(granted) {
  if (!granted) return "";
  const from = dayOf(granted.valid_from);
  const until = dayOf(granted.valid_until);
  const valid = from && until ? `${from} to ${until}` : from ? `from ${from}` : until ? `until ${until}` : "";
  const days = Number(granted.days) > 0 ? `${granted.days} ${Number(granted.days) === 1 ? "day" : "days"}` : "";
  return [ENTRY_WORDS[granted.entries], valid, days].filter(Boolean).join(" · ");
}

// ---- the refusal call -------------------------------------------------------

export const refusalCallDue = (caseData) => caseData?.status === "rejected" && !!caseData.decision && caseData.decision.outcome !== "approved" && !caseData.decision.called_at;

// The customer's phone: the case's own when the API sends one, else the
// first traveller's answer to the phone question.
export function customerPhone(caseData) {
  const own = String(caseData?.customer?.phone || "").trim();
  if (own) return own;
  for (const traveller of caseData?.travellers || []) {
    const fields = (traveller.sections || []).flatMap((s) => s.fields || []);
    const field = fields.find((f) => f.key === "phone" && String(f.value || "").trim());
    if (field) return String(field.value).trim();
  }
  return "";
}

// "tel:+9613123456" from "+961 3 123 456", or "" when it holds no digits.
export function telHref(phone) {
  const text = String(phone || "").trim();
  const digits = text.replace(/[^\d]/g, "");
  if (!digits) return "";
  return `tel:${text.startsWith("+") ? "+" : ""}${digits}`;
}

// ---- codes from the customer -----------------------------------------------

export const CODE_FRESH_MS = 60 * 60 * 1000;

// Sent in the last hour (a few minutes ahead is the two clocks disagreeing,
// not the future). A code with no time is never fresh.
export function codeIsFresh(code, now = Date.now()) {
  const at = instantOf(code?.sent_at);
  if (Number.isNaN(at)) return false;
  const age = now - at;
  return age <= CODE_FRESH_MS && age >= -5 * 60 * 1000;
}

// The customer's codes, newest first (the API sends them so; sorted again
// in case).
export function codesOf(caseData) {
  const list = Array.isArray(caseData?.codes) ? caseData.codes.filter((c) => c && String(c.text || "").trim()) : [];
  return [...list].sort((a, b) => (instantOf(b.sent_at) || 0) - (instantOf(a.sent_at) || 0));
}

// The newest code from the last hour that isn't used (the API marks codes
// used when staff press Mark as used or tick a filing step after them):
// it goes at the top of the case.
export function freshCode(caseData, now = Date.now()) {
  return codesOf(caseData).find((code) => !code.used && codeIsFresh(code, now)) || null;
}

// "14:02 · 12 min ago", "14:02 · just now".
export function receivedWords(sentAt, now = Date.now()) {
  const at = instantOf(sentAt);
  if (Number.isNaN(at)) return "";
  const clock = new Date(at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const minutes = Math.floor((now - at) / 60000);
  const ago = minutes < 1 ? "just now" : minutes < 60 ? `${minutes} min ago` : `${Math.floor(minutes / 60)} h ago`;
  return `${clock} · ${ago}`;
}

// ---- the host's attestation -------------------------------------------------

export const HOST_STEPS = [
  { status: "sent", label: "Link sent" },
  { status: "applied", label: "At the town hall" },
  { status: "ready", label: "Attestation ready" },
  { status: "posted", label: "Original posted" },
  { status: "received", label: "Received" },
];

export const hostStep = (status) => Math.max(HOST_STEPS.findIndex((s) => s.status === status), 0);

// ---- fees the customer paid -------------------------------------------------

// The first fee the customer says is paid and staff haven't ticked.
export function feeToCheck(filing) {
  if (!filing?.started) return null;
  return (filing.steps || []).find((s) => s.kind === "fee" && s.customer_paid_at && !s.done) || null;
}

// The API's own next step (the case's next_step: use_code, call_refusal or
// check_receipt, else null), as the steps below. null when the API sends
// none at all (an older API): then the CMS works it out itself.
export function stepsFromApi(caseData, now = Date.now()) {
  const next = caseData?.next_step;
  if (next === undefined) return null;
  if (!next) return [];
  if (next.kind === "use_code") {
    const code = { text: String(next.code ?? ""), sent_at: next.sent_at };
    // Past the hour on this clock: the case is read again for what's next.
    return code.text && codeIsFresh(code, now) ? [{ kind: "code", code }] : [];
  }
  if (next.kind === "call_refusal") {
    return [{ kind: "call", title: next.text || null, phone: customerPhone(caseData), appeal: caseData.decision?.appeal?.deadline || null }];
  }
  if (next.kind === "check_receipt") {
    const fee = (caseData.filing?.steps || []).find((s) => Number(s.id) === Number(next.step_id)) || { id: next.step_id, title: "A fee", receipt: false };
    return [{ kind: "receipt", title: next.text || null, fee }];
  }
  return [];
}

// The steps that come before the usual next step, most urgent first: a fresh
// code, then the refusal call, then a receipt to check. The API's next_step
// when it sends one; this order only for an API without it.
export function urgentSteps(caseData, { now = Date.now() } = {}) {
  const fromApi = stepsFromApi(caseData, now);
  if (fromApi) return fromApi;
  const steps = [];
  const code = freshCode(caseData, now);
  if (code) steps.push({ kind: "code", code });
  if (refusalCallDue(caseData)) {
    steps.push({ kind: "call", phone: customerPhone(caseData), appeal: caseData.decision.appeal?.deadline || null });
  }
  const fee = feeToCheck(caseData.filing);
  if (fee) steps.push({ kind: "receipt", fee });
  return steps;
}

// The 503 an endpoint gives before migration 023.
export const needsMigration = (error) => error?.status === 503;

// What to say when a call failed: the server's own sentence for a rule it
// explains (4xx, "Record the decision first"), else the caller's words.
export const failureWords = (error, fallback) =>
  error?.status >= 400 && error?.status < 500 && error.status !== 401 && error.message ? error.message : fallback;
